/**
 * Collect match IDs from React Query cache for Socket.io subscribe:matches (live score rooms).
 */

import type { QueryClient } from '@tanstack/react-query';

function addMatchIdsFromChallenges(data: unknown, add: (id: string) => void): void {
  if (!Array.isArray(data)) return;
  for (const c of data) {
    if (!c || typeof c !== 'object') continue;
    const matchIds = (c as { matchIds?: unknown }).matchIds;
    if (!Array.isArray(matchIds)) continue;
    for (const id of matchIds) {
      if (typeof id === 'string' && id.trim()) add(id.trim());
    }
  }
}

function addMatchIdsFromMatchRows(data: unknown, add: (id: string) => void): void {
  if (!Array.isArray(data)) return;
  for (const m of data) {
    if (!m || typeof m !== 'object') continue;
    const id = (m as { id?: unknown }).id;
    if (typeof id === 'string' && id.trim()) add(id.trim());
  }
}

function addMatchIdsFromInfiniteGroupChallenges(data: unknown, add: (id: string) => void): void {
  if (!data || typeof data !== 'object') return;
  const pages = (data as { pages?: unknown }).pages;
  if (!Array.isArray(pages)) return;
  for (const page of pages) {
    if (!page || typeof page !== 'object') continue;
    const inner = (page as { data?: unknown }).data;
    addMatchIdsFromChallenges(inner, add);
  }
}

/**
 * Walks TanStack Query cache for challenges, group challenges, matches, and game results.
 */
export function collectMatchIdsForLiveSubscriptions(
  queryClient: QueryClient,
  maxIds: number,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  const add = (id: string) => {
    const t = id.trim();
    if (!t || seen.has(t) || out.length >= maxIds) return;
    seen.add(t);
    out.push(t);
  };

  const queries = queryClient.getQueryCache().getAll();
  for (const q of queries) {
    if (out.length >= maxIds) break;
    const data = q.state.data;
    if (data === undefined || data === null) continue;
    const key = q.queryKey;
    if (!Array.isArray(key)) continue;

    const k0 = key[0];

    if (k0 === 'challenges') {
      if (key[1] === 'detail' && typeof data === 'object') {
        const matchIds = (data as { matchIds?: unknown }).matchIds;
        if (Array.isArray(matchIds)) {
          for (const id of matchIds) {
            if (typeof id === 'string') add(id);
            if (out.length >= maxIds) break;
          }
        }
      } else if (Array.isArray(data)) {
        addMatchIdsFromChallenges(data, add);
      }
      continue;
    }

    if (k0 === 'groups' && key[2] === 'challenges') {
      if (key[3] === 'infinite') {
        addMatchIdsFromInfiniteGroupChallenges(data, add);
      } else if (Array.isArray(data)) {
        addMatchIdsFromChallenges(data, add);
      }
      continue;
    }

    if (k0 === 'matches' && Array.isArray(data)) {
      addMatchIdsFromMatchRows(data, add);
      continue;
    }

    if (k0 === 'gameResults' && key[1] === 'results' && Array.isArray(data)) {
      addMatchIdsFromMatchRows(data, add);
    }
  }

  return out;
}
